import type { Maybe } from "@/graphql/generated-types";
import { minutesToTime, toUnix } from "@/stories/utils/dates";
import joi from "joi";

export type SignupFeatureState = {
  signup: boolean;
  signupClose: number;
  signupCloseBefore: boolean;
  signupCloseDays: number;
  signupCloseHours: number;
  signupCloseMinutes: number;
  signupEnd: number;
  maxSignups: number;
  notifyNokOnSignup?: boolean;
};

export type DatabaseModel = {
  signupClose?: number | null | undefined;
  signupEnabled: boolean;
  maxSignups?: Maybe<number> | undefined;
  notifyNokOnSignup?: boolean;
};

export type SignupInput = {
  signupClose: number | null;
  maxSignup: number;
  signup: boolean;
  notifyNokOnSignup?: boolean;
};

type GetItemOptions = {
  useNotifyNokOnSignup: boolean;
};

const DEFAULT_GET_ITEM_OPTIONS: GetItemOptions = {
  useNotifyNokOnSignup: true,
};

export const SIGNUP_FEATURE = {
  getDefault() {
    return {
      signupEnabled: true,
      signupCloseDays: 0,
      signupCloseHours: 0,
      signupCloseMinutes: 0,
      signup: true,
      signupClose: 0,
      signupCloseBefore: false,
      signupEnd: toUnix(new Date()),
      maxSignups: 0,
      notifyNokOnSignup: false,
    };
  },

  setState(data: DatabaseModel, state: SignupFeatureState) {
    // Signup options
    if (data.signupClose) {
      state.signupCloseBefore = true;
      const { days, hours, minutes } = minutesToTime(data.signupClose);
      state.signupCloseDays = days;
      state.signupCloseHours = hours;
      state.signupCloseMinutes = minutes;
    }

    state.signup = data.signupEnabled;
    state.maxSignups = data.maxSignups || 0;
    state.notifyNokOnSignup = data.notifyNokOnSignup || false;
  },

  getItem(
    state: SignupFeatureState,
    options: GetItemOptions = DEFAULT_GET_ITEM_OPTIONS,
  ) {
    const result: Partial<SignupInput> = {};

    if (state.signup && state.signupCloseBefore) {
      const daysInMinutes = state.signupCloseDays
        ? state.signupCloseDays * 1440
        : 0;
      const hoursInMinutes = state.signupCloseHours
        ? state.signupCloseHours * 60
        : 0;
      const minutes = state.signupCloseMinutes ? state.signupCloseMinutes : 0;

      result.signupClose = daysInMinutes + hoursInMinutes + minutes;
    } else {
      result.signupClose = 0;
    }

    // Let's set the maxSignup value
    result.maxSignup = 0;
    if (state.signup && state.maxSignups) {
      result.maxSignup = state.maxSignups;
    }

    result.signup = state.signup;

    if (options.useNotifyNokOnSignup) {
      result.notifyNokOnSignup = state.notifyNokOnSignup || false;
    }

    return result;
  },

  getValidationRules() {
    return {
      maxSignups: joi.number().max(999999),
    };
  },
};
